import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from 'components/ui';
import { Button } from 'components/ui';
import { OnboardingData } from '../types';
import { cn } from 'lib/utils';

interface Step8SummaryProps {
  data: OnboardingData;
  onEditStep: (step: number) => void;
  isSubmitting: boolean;
}

const Step8Summary: React.FC<Step8SummaryProps> = ({ data, onEditStep, isSubmitting }) => {
  const navigate = useNavigate();
  
  const formatArray = (arr: string[] | undefined) => {
    if (!arr || arr.length === 0) return 'Not specified';
    return arr.join(', ');
  };

  const formatFileSize = (files: any[] | undefined) => {
    if (!files || files.length === 0) return 'No files uploaded';
    return `${files.length} file(s) uploaded`;
  };

  const summarySections = [
    {
      title: 'Product Features & Customer Feedback',
      step: 1,
      data: [
        {
          label: 'Brand Name',
          value: data.step1_productFeatures?.brandName || 'Not specified',
        },
        {
          label: 'Top Selling Product',
          value: data.step1_productFeatures?.topSellingProduct || 'Not specified',
        },
        {
          label: 'What People Like',
          value: data.step1_productFeatures?.whatPeopleLike || 'Not specified',
        },
        {
          label: 'Upcoming Dates',
          value: data.step1_productFeatures?.upcomingDates || 'Not specified',
        },
        {
          label: 'Features/Benefits',
          value: data.step1_productFeatures?.features || 'Not specified',
        },
        { label: 'Use Cases', value: formatArray(data.step1_productFeatures?.useCases) },
        {
          label: 'Target Industries',
          value: data.step1_productFeatures?.targetIndustries || 'Not specified',
        },
      ],
    },
    {
      title: 'Unique Selling Propositions & Brand Identity',
      step: 2,
      data: [
        {
          label: 'Business Purpose',
          value: data.step2_brandIdentity?.businessPurpose || 'Not specified',
        },
        {
          label: 'Brand Passion',
          value: data.step2_brandIdentity?.brandPassion || 'Not specified',
        },
        { label: 'Brand Keywords', value: formatArray(data.step2_brandIdentity?.brandKeywords) },
      ],
    },
    {
      title: 'Competitive Landscape',
      step: 3,
      data: [
        { label: 'Competitors', value: formatArray(data.step3_competitiveLandscape?.competitors) },
        {
          label: 'Differentiation',
          value: data.step3_competitiveLandscape?.differentiation || 'Not specified',
        },
      ],
    },
    {
      title: 'Target Market & Customer Demographics',
      step: 4,
      data: [
        {
          label: 'Customer Personas',
          value: formatArray(data.step4_targetMarket?.customerPersonas),
        },
        {
          label: 'Customer Personas Description',
          value: data.step4_targetMarket?.customerPersonasDescription || 'Not specified',
        },
        { label: 'Pain Points', value: data.step4_targetMarket?.painPoints || 'Not specified' },
        { label: 'Geographic Scale', value: data.step4_targetMarket?.scale || 'Not specified' },
        { label: 'Countries Served', value: formatArray(data.step4_targetMarket?.areasServed) },
        {
          label: 'Physical Location',
          value: data.step4_targetMarket?.hasPhysicalLocation === 'yes'
            ? data.step4_targetMarket?.physicalAddress || 'Yes'
            : 'No',
        },
      ],
    },
    {
      title: 'Business Goals & Future Plans',
      step: 5,
      data: [
        {
          label: 'Business Model',
          value: data.step5_businessGoals?.businessModel || 'Not specified',
        },
        {
          label: 'Marketing/SEO Budget',
          value: data.step5_businessGoals?.budget || 'Not specified',
        },
        {
          label: 'Ongoing Marketing',
          value: data.step5_businessGoals?.ongoingMarketing || 'Not specified',
        },
        { label: 'PR Agency', value: data.step5_businessGoals?.prAgency || 'Not specified' },
        { label: '1 Year Goals', value: data.step5_businessGoals?.goals1Year || 'Not specified' },
        { label: '3 Year Goals', value: data.step5_businessGoals?.goals3Years || 'Not specified' },
        { label: '5 Year Goals', value: data.step5_businessGoals?.goals5Years || 'Not specified' },
      ],
    },
    {
      title: "Company's Online Presence",
      step: 6,
      data: [
        {
          label: 'Website Manager',
          value: data.step6_onlinePresence?.websiteManager || 'Not specified',
        },
        { label: 'Brand Voice', value: data.step6_onlinePresence?.brandVoice || 'Not specified' },
        {
          label: 'Acquisition Channels',
          value: data.step6_onlinePresence?.acquisitionChannels || 'Not specified',
        },
        { label: 'Sales Cycle', value: data.step6_onlinePresence?.salesCycle || 'Not specified' },
        // { label: 'Uploaded Content', value: formatFileSize(data.step6_onlinePresence?.uploadedFiles) },
        { label: 'Team Size', value: data.step6_onlinePresence?.teamSize || 'Not specified' },
        {
          label: 'Content Capacity',
          value: data.step6_onlinePresence?.contentCapacity || 'Not specified',
        },
      ],
    },
    {
      title: 'Brand Assets',
      step: 7,
      data: [
        { label: 'Brand Assets Files', value: formatFileSize(data.step7_brandAssets?.files) },
        { label: 'Brand Tone', value: data.step7_brandAssets?.tone || 'Not specified' },
        { label: 'Words to Avoid', value: formatArray(data.step7_brandAssets?.wordsToAvoid) },
        { label: 'Key Themes', value: formatArray(data.step7_brandAssets?.keyThemes) },
        {
          label: 'Twitter Examples',
          value: formatArray(data.step7_brandAssets?.socialExamples?.twitterExamples),
        },
        {
          label: 'LinkedIn Examples',
          value: formatArray(data.step7_brandAssets?.socialExamples?.linkedinExamples),
        },
        {
          label: 'Blog URLs',
          value: formatArray(data.step7_brandAssets?.socialExamples?.blogUrls),
        },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Re-import Banner */}
      <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-md text-sm">
        <span className="text-gray-600">
          Need to update your brand profile?{' '}
          <button
            type="button"
            onClick={() => navigate('/onboarding?step=0')}
            className="text-primary hover:underline font-medium"
          >
            Re-import from documents
          </button>
        </span>
      </div>

      {/* Summary Sections */}
      <div className="space-y-4">
        {summarySections.map((section) => (
          <div
            key={section.step}
            className=" mx-auto mt-10 p-4 pb-7 bg-white bg-opacity-30 backdrop-blur-lg border border-gray-400 border-white border-opacity-20 rounded-lg shadow-xl"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex gap-2 md:gap-4 items-center min-w-0">
                <Icon type="check" className="w-5 md:w-6 shrink-0" />
                <h2 className="text-base md:text-lg font-semibold text-gray-800 truncate">{section.title}</h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEditStep(section.step)}
                className="text-link w-fit hover:text-link"
              >
                Edit
              </Button>
            </div>

            <div className="space-y-2 ml-0 md:ml-10">
              {section.data.map((item, index) => (
                <div key={index} className="flex flex-col md:grid md:grid-cols-5 gap-0 md:gap-3 md:items-start md:space-x-2">
                  <h4 className="text-sm font-medium text-black">{item.label}:</h4>
                  <p
                    className={cn(
                      'text-sm md:col-span-4 text-gray-800 md:w-2/3 truncate line-clamp-1',
                      item.value === 'Not specified' && 'text-gray-400 italic',
                    )}
                  >
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Final CTA */}
      <div className="text-center pt-6">
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>Saving workspace...</span>
            </div>
          ) : (
            'Save my workspace'
          )}
        </Button>

        <p className="text-sm text-text-secondary mt-4">
          You can always update these settings later in your workspace.
        </p>
      </div>
    </div>
  );
};

export { Step8Summary };
